/**
 * RoleSelect Component (ORCH-336, PRD User Manager UM-6)
 *
 * Purpose: The one role picker: catalog roles by name with their description,
 *          grouped Standard Roles / Custom Roles, A to Z. Used by the Access
 *          Rights section, the invite dialog and the user list's role filter.
 * Logic: Built on the same MUI Autocomplete as ListFieldRenderer (outlined
 *        field, floating label, ValueChip tags). A role the caller can't assign
 *        isn't offered. One the person already holds stays as a read-only chip,
 *        so saving never drops it. The server decides which roles are
 *        assignable (`role.assignable`) and enforces it again on save.
 * Dependencies: MUI Autocomplete, ValueChip
 */

import React, { useMemo } from 'react';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import { Role } from '../../types/authz';
import GroupHeading from './GroupHeading';
import ValueChip from './ValueChip';

const GLOBAL = 'GLOBAL';

export const STANDARD_ROLES = 'Standard Roles';
export const CUSTOM_ROLES = 'Custom Roles';

/** The role fields the picker reads; a member's held roles have the same shape. */
export type RoleOption = Pick<Role, 'role_id' | 'name' | 'tenant_id'> & {
  description?: string | null;
  status?: string;
  assignable?: boolean;
};

interface RoleSelectBaseProps {
  /** The role catalog (GET /authz/roles). */
  roles: RoleOption[];
  label?: string;
  required?: boolean;
  disabled?: boolean;
  /** Offer every active role, assignable or not (the list's role filter). */
  offerAll?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  placeholder?: string;
  size?: 'small' | 'medium';
  dataTestId?: string;
  sx?: object;
}

interface RoleSelectMultipleProps extends RoleSelectBaseProps {
  multiple?: true;
  /** Selected role ids. */
  value: string[];
  onChange: (roleIds: string[]) => void;
  /** Roles the person already holds, so one missing from the catalog still shows. */
  held?: RoleOption[];
}

interface RoleSelectSingleProps extends RoleSelectBaseProps {
  multiple: false;
  value: string | null;
  onChange: (roleId: string | null) => void;
  held?: undefined;
}

export type RoleSelectProps = RoleSelectMultipleProps | RoleSelectSingleProps;

const groupOf = (role: RoleOption) => (role.tenant_id === GLOBAL ? STANDARD_ROLES : CUSTOM_ROLES);

const byGroupThenName = (a: RoleOption, b: RoleOption) =>
  (groupOf(a) === STANDARD_ROLES ? 0 : 1) - (groupOf(b) === STANDARD_ROLES ? 0 : 1) ||
  a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });

const RoleSelect: React.FC<RoleSelectProps> = (props) => {
  const {
    roles,
    label = 'Role(s)',
    required,
    disabled,
    offerAll,
    error,
    helperText,
    placeholder,
    size,
    dataTestId = 'role-select',
    sx,
  } = props;

  const held = props.multiple === false ? undefined : props.held;
  const selectedIds = useMemo(
    () => (props.multiple === false ? (props.value ? [props.value] : []) : props.value),
    [props.multiple, props.value]
  );

  // Every role the picker may need to show: the catalog plus anything already held.
  const known = useMemo(() => {
    const byId = new Map<string, RoleOption>();
    (held || []).forEach((r) => byId.set(r.role_id, { ...r, assignable: false }));
    roles.forEach((r) => byId.set(r.role_id, r));
    return byId;
  }, [roles, held]);

  const canPick = (role: RoleOption) => offerAll || role.assignable === true;

  const options = useMemo(
    () =>
      Array.from(known.values())
        .filter(
          (r) =>
            (r.status ?? 'active') === 'active' && (canPick(r) || selectedIds.includes(r.role_id))
        )
        .sort(byGroupThenName),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [known, selectedIds, offerAll]
  );

  const selected = selectedIds
    .map((id) => known.get(id))
    .filter((r): r is RoleOption => !!r)
    .sort(byGroupThenName);

  const common = {
    options,
    disabled,
    size,
    groupBy: groupOf,
    getOptionLabel: (role: RoleOption) => role.name,
    isOptionEqualToValue: (option: RoleOption, value: RoleOption) =>
      option.role_id === value.role_id,
    // A held role the caller can't assign is shown, not offered
    getOptionDisabled: (role: RoleOption) => !canPick(role),
    // The shared grouped-list heading (ORCH-410): the same band as every grouped list field
    renderGroup: (params: { key: React.Key; group: string; children?: React.ReactNode }) => (
      <GroupHeading
        key={params.key}
        group={params.group}
        top={-8}
        dataTestId={`${dataTestId}-group`}
      >
        {params.children}
      </GroupHeading>
    ),
    renderOption: (optionProps: React.HTMLAttributes<HTMLLIElement>, role: RoleOption) => (
      <Box
        component="li"
        {...optionProps}
        key={role.role_id}
        data-testid={`${dataTestId}-option-${role.name}`}
        sx={{ pl: '28px !important', flexDirection: 'column', alignItems: 'flex-start !important' }}
      >
        <Typography variant="body2">{role.name}</Typography>
        {role.description && (
          <Typography variant="caption" color="text.secondary">
            {role.description}
          </Typography>
        )}
      </Box>
    ),
    renderInput: (params: React.ComponentProps<typeof TextField>) => (
      <TextField
        {...params}
        label={label}
        required={required}
        error={error}
        helperText={helperText}
        placeholder={selected.length ? undefined : placeholder}
        fullWidth
        margin="none"
      />
    ),
  };

  if (props.multiple === false) {
    return (
      <Box sx={sx} data-testid={dataTestId}>
        <Autocomplete<RoleOption, false>
          {...common}
          value={selected[0] ?? null}
          onChange={(_, role) => props.onChange(role ? role.role_id : null)}
        />
      </Box>
    );
  }

  const { onChange } = props;
  return (
    <Box sx={sx} data-testid={dataTestId}>
      <Autocomplete<RoleOption, true>
        {...common}
        multiple
        disableCloseOnSelect
        value={selected}
        onChange={(_, next) => {
          // Read-only roles can't be removed here (Backspace, clear), only kept
          const kept = selected.filter((r) => !canPick(r) && !next.includes(r));
          onChange([...kept, ...next].map((r) => r.role_id));
        }}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((role, index) => {
            const { key, onDelete, ...tagProps } = getTagProps({ index });
            const locked = !canPick(role);
            return (
              <ValueChip
                key={key}
                label={locked ? `${role.name} (Read-Only)` : role.name}
                size="small"
                data-testid={`${dataTestId}-chip-${role.name}`}
                {...tagProps}
                onDelete={locked ? undefined : onDelete}
              />
            );
          })
        }
      />
    </Box>
  );
};

export default RoleSelect;
